'use client';
import { useRef, useEffect, useState } from 'react';
import { popularCities, findCity } from '@/lib/israelCities';
import { suggestCities } from '@/lib/searchText'; // typos, keyboard-layout slips, English (2026-09-28)
import { loadGoogleMaps } from '@/lib/googleMaps';

type Coords = { lat: number; lng: number };

// City picker: a full-width 3-row horizontal slider of cities (by population).
// "עיר אחרת" (controlled by the parent, lives in the question title row) opens a
// Google Maps Places autocomplete (AJAX) for any other city.
export default function CityQuestion({ onSelect, otherOpen }: { onSelect: (name: string, coords?: Coords) => void; otherOpen: boolean }) {
  // Founder 2026-09-22: a live city search box sits in the grid's first cell (same size as a chip) — typing filters
  // the chips in place, no toggle, no extra row.
  const [query, setQuery] = useState('');
  const cities = query.trim() ? suggestCities(query, 36) : popularCities(36);
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<any>(null);

  useEffect(() => {
    if (!otherOpen) return;
    loadGoogleMaps(() => {
      if (!inputRef.current || acRef.current || !window.google?.maps?.places) return;
      try {
        const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['(cities)'],
          componentRestrictions: { country: 'il' },
          fields: ['name', 'geometry'],
        });
        ac.addListener('place_changed', () => {
          const p = ac.getPlace();
          const name = (p?.name || inputRef.current?.value || '').trim();
          const loc = p?.geometry?.location;
          if (name && loc) onSelect(name, { lat: loc.lat(), lng: loc.lng() });
          else if (name) onSelect(name);
        });
        acRef.current = ac;
        inputRef.current.focus();
      } catch {}
    });
  }, [otherOpen, onSelect]);

  function submitTyped() {
    const v = inputRef.current?.value?.trim();
    if (!v) return;
    const c = findCity(v);
    onSelect(v, c ? { lat: c.lat, lng: c.lng } : undefined);
  }

  function pick(name: string) {
    const c = findCity(name);
    onSelect(name, c ? { lat: c.lat, lng: c.lng } : undefined);
  }

  return (
    <div>
      {/* Cities — full-width 3-row grid that scrolls horizontally (no dead space) */}
      <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
        <div style={{ display: 'grid', gridTemplateRows: 'repeat(3, auto)', gridAutoFlow: 'column', gap: 8, width: 'max-content' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 10px 0 12px', borderRadius: 16, border: '1.5px solid #93c5fd', background: '#eff6ff', width: 170, boxSizing: 'border-box' }}>
            <span style={{ fontSize: 14 }}>🔍</span>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="חיפוש עיר..." aria-label="חיפוש עיר"
              onKeyDown={e => { if (e.key === 'Enter' && query.trim()) { e.preventDefault(); pick(cities[0] || query.trim()); } }}
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0, color: '#0f172a', fontFamily: 'inherit', padding: '9px 0' }} />
          </div>
          {!cities.length && <span style={{ fontSize: 12.5, color: '#64748b', alignSelf: 'center' }}>לא נמצאה עיר — Enter ישלח את מה שהקלדת</span>}
          {cities.map(c => (
            <button key={c} onClick={() => pick(c)}
              style={{
                whiteSpace: 'nowrap',
                minHeight: 44, padding: '9px 16px', borderRadius: 16,
                border: '1.5px solid #e0f2fe', background: '#f0f9ff',
                color: '#0369a1', fontWeight: 700, fontSize: 13.5,
                cursor: 'pointer', transition: 'all .14s', fontFamily: 'inherit',
              }}
              onMouseEnter={e => Object.assign((e.currentTarget as HTMLElement).style, { background: '#0d47a1', color: '#fff', borderColor: '#0d47a1' })}
              onMouseLeave={e => Object.assign((e.currentTarget as HTMLElement).style, { background: '#f0f9ff', color: '#0369a1', borderColor: '#e0f2fe' })}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Google-connected free-text city (toggled by "עיר אחרת" in the title row) */}
      {otherOpen && (
        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
          <input
            ref={inputRef}
            placeholder="הקלידו עיר — חיפוש Google Maps..."
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitTyped(); } }}
            style={{
              flex: 1, minWidth: 0, padding: '10px 13px', borderRadius: 14,
              border: '1.5px solid #93c5fd', outline: 'none', fontSize: 13.5,
              color: '#0f172a', fontFamily: 'inherit', background: '#fff',
            }}
          />
          <button onClick={submitTyped}
            style={{ flexShrink: 0, background: '#0d47a1', color: '#fff', border: 'none', borderRadius: 12, padding: '0 16px', cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>
            ✓
          </button>
        </div>
      )}
    </div>
  );
}
